import { useEffect, useSyncExternalStore } from "react";
import { useApiClient } from "@/hooks/useApi";
import { useSession, type SessionContextValue } from "@/auth/SessionProvider";
import { type ApiClient, ApiError } from "@/lib/api";
import { canonicalSubject, parseAck, parseEvidence, parseJob, parseQuote, parseRecovery, canonicalDomain, type PulseBinding, type PulseEvidence, type PulseQuote } from "@/lib/pulseContract";
export type PulsePhase = "idle" | "quoting" | "quote" | "expired" | "submitting" | "unknown" | "processing" | "paused" | "delivered" | "partial" | "unbilled" | "failed" | "unavailable" | "denied" | "error";
interface Attempt extends PulseBinding {
    key: string;
    jobId: string | null;
}
interface State {
    phase: PulsePhase;
    quote: PulseQuote | null;
    attempt: Attempt | null;
    evidence: PulseEvidence | null;
    observing: boolean;
}
const INITIAL: State = { phase: "idle", quote: null, attempt: null, evidence: null, observing: false };
/** One in-memory attempt. Owned reads share a single budget; no retry invokes submit. */
export class PulseAttemptController {
    private state: State = { ...INITIAL };
    private listeners = new Set<() => void>();
    private generation = 0;
    private disposed = false;
    private busy = false;
    private active = new Set<AbortController>();
    private wake: (() => void) | null = null;
    constructor(private api: ApiClient, private org: string, private raw: {
        type: "company" | "contact";
        key: string;
    }) { }
    snapshot = (): State => this.state;
    subscribe = (listener: () => void): (() => void) => { this.listeners.add(listener); return () => this.listeners.delete(listener); };
    private set(update: Partial<State>): void { if (this.disposed)
        return; this.state = { ...this.state, ...update }; this.listeners.forEach(l => l()); }
    private current(g: number): boolean { return !this.disposed && g === this.generation; }
    private async request<T>(g: number, run: (signal: AbortSignal) => Promise<T>): Promise<T> {
        const c = new AbortController();
        this.active.add(c);
        let timer: ReturnType<typeof setTimeout> | undefined;
        try {
            return await Promise.race([run(c.signal), new Promise<never>((_, reject) => {
                    c.signal.addEventListener("abort", () => reject(new Error("Observation stopped")), { once: true });
                    timer = setTimeout(() => { reject(new Error("Request deadline")); c.abort(); }, 15000);
                })]);
        }
        finally {
            clearTimeout(timer);
            this.active.delete(c);
            if (!this.current(g))
                c.abort();
        }
    }
    async getQuote(): Promise<void> {
        if (this.disposed || this.busy || this.state.attempt)
            return;
        this.busy = true;
        const g = this.generation;
        this.set({ phase: "quoting", quote: null, evidence: null });
        try {
            const subject = await canonicalSubject(this.raw);
            if (!this.current(g))
                return;
            const raw = await this.request(g, signal => this.api.post<unknown>("/quotes", { action: "enrich_context", unit_count: 1, payload_hash: subject.hash }, { signal }));
            const quote = parseQuote(raw, subject, this.org, Date.now());
            if (this.current(g))
                this.set({ phase: "quote", quote });
        }
        catch (e) {
            if (this.current(g))
                this.set({ phase: e instanceof ApiError && [401, 403].includes(e.httpStatus) ? "denied" : "error" });
        }
        finally {
            if (this.current(g))
                this.busy = false;
        }
    }
    async submit(consent: boolean): Promise<void> {
        const q = this.state.quote;
        if (this.disposed || this.busy || this.state.attempt || !q || !consent || this.state.phase !== "quote")
            return;
        if (Date.parse(q.expiresAt) <= Date.now()) {
            this.set({ phase: "expired", quote: null });
            return;
        }
        this.busy = true;
        const g = this.generation;
        try {
            const subject = await canonicalSubject(this.raw);
            if (!this.current(g))
                return;
            if (Date.parse(q.expiresAt) <= Date.now()) {
                this.set({ phase: "expired", quote: null });
                return;
            }
            const attempt: Attempt = { subject, quoteId: q.id, key: crypto.randomUUID(), jobId: null };
            this.set({ attempt, phase: "submitting" });
            try {
                const raw = await this.request(g, signal => this.api.post<unknown>("/context/enrich", { subject: { type: subject.type, key: subject.input }, quote_id: q.id, idempotency_key: attempt.key, enrichment_consent: true }, { signal }));
                const jobId = parseAck(raw, attempt);
                if (!this.current(g))
                    return;
                this.set({ attempt: { ...attempt, jobId }, phase: "processing" });
            }
            catch (e) {
                if (this.current(g))
                    this.set({ phase: e instanceof ApiError && [401, 403].includes(e.httpStatus) ? "denied" : "unknown", evidence: null });
            }
        }
        catch {
            if (this.current(g))
                this.set({ phase: "error" });
        }
        finally {
            if (this.current(g)) {
                this.busy = false;
                if (this.snapshot().phase === "processing")
                    void this.check();
            }
        }
    }
    async check(): Promise<void> {
        if (this.disposed || this.busy || this.state.observing || !this.state.attempt)
            return;
        const g = this.generation;
        let count = 0;
        const start = Date.now();
        this.set({ observing: true, evidence: null, phase: this.state.attempt.jobId ? "processing" : "unknown" });
        const ceiling = setTimeout(() => { if (this.current(g)) {
            this.stop();
        } }, 240000);
        const read = async <T,>(run: (signal: AbortSignal) => Promise<T>): Promise<T> => {
            if (!this.current(g) || count >= 30 || Date.now() - start >= 240000)
                throw new Error("Observation budget");
            if(count>0) await new Promise<void>(resolve => { const t = setTimeout(() => { this.wake = null; resolve(); }, count <= 5 ? 2000 : count <= 15 ? 5000 : 10000); this.wake = () => { clearTimeout(t); this.wake = null; resolve(); }; });
            if(!this.current(g) || Date.now()-start>=240000)throw new Error("Observation budget");
            count++;
            return this.request(g, run);
        };
        try {
            while (this.current(g)) {
                const a = this.state.attempt!;
                if (!a.jobId) {
                    const raw = await read(signal => this.api.get<unknown>("/jobs", { signal, query: { idempotency_key: a.key, limit: 1, offset: 0 } }));
                    const recovered = parseRecovery(raw, a);
                    if (!this.current(g))
                        return;
                    if (!recovered) {
                        this.set({ phase: "unknown" });
                        return;
                    }
                    this.set({ attempt: { ...a, jobId: recovered.id }, phase: "processing" });
                }
                const bound = this.state.attempt!, id = bound.jobId!;
                const job = parseJob(await read(signal => this.api.get<unknown>(`/jobs/${id}`, { signal })), id);
                if (!this.current(g))
                    return;
                if (["succeeded", "failed", "cancelled"].includes(job.status)) {
                    const result = parseEvidence(await read(signal => this.api.get<unknown>(`/jobs/${id}/results`, { signal })), bound, id);
                    if (!this.current(g))
                        return;
                    if (result.state !== "pending") {
                        this.set({ evidence: result, phase: result.state === "failed" ? "failed" : result.state === "unavailable" ? "unavailable" : result.marker === "pulse.enriched.unbilled" ? "unbilled" : result.outcome === "partial" ? "partial" : "delivered" });
                        return;
                    }
                }
                if (count >= 30 || Date.now() - start >= 240000) {
                    this.set({ phase: "paused" });
                    return;
                }
            }
        }
        catch (e) {
            if (this.current(g))
                this.set({ phase: e instanceof ApiError && [401, 403].includes(e.httpStatus) ? "denied" : count >= 30 || Date.now() - start >= 240000 ? "paused" : "unknown", evidence: null });
        }
        finally {
            clearTimeout(ceiling);
            if (this.current(g))
                this.set({ observing: false });
        }
    }
    stop(): void { this.generation++; this.busy = false; this.active.forEach(c => c.abort()); this.active.clear(); this.wake?.(); this.set({ observing: false, phase: this.state.attempt ? "paused" : this.state.phase }); }
    suspend(): void { this.stop(); this.set({ quote: null, evidence: null, phase: this.state.attempt ? (this.state.attempt.jobId ? "paused" : "unknown") : "idle" }); }
    dispose(): void { this.generation++; this.active.forEach(c=>c.abort());this.active.clear();this.wake?.();this.state = { ...INITIAL }; this.disposed = true; this.listeners.clear(); }
}
let memoryOwner: string | null = null;
let memoryRevision = 0;
const memoryListeners = new Set<() => void>();
const subscribeMemory = (listener: () => void): (() => void) => { memoryListeners.add(listener); return () => { memoryListeners.delete(listener); }; };
const memorySnapshot = (): number => memoryRevision;
const attempts = new Map<string, PulseAttemptController>();
let memoryPrincipal:string|null=null,memorySession="";
let watchClient:SessionContextValue["supabase"]|null=null;
let authWatch:{unsubscribe:()=>void}|null=null;
let authGeneration = 0;
let scopeWatch: (() => void) | null = null;
let scopeRegistration: SessionContextValue["subscribeScopeInvalidation"] | null = null;
function clearMemory():void {scopeWatch?.();scopeWatch=null;scopeRegistration=null;attempts.forEach(c=>c.dispose());attempts.clear();memoryOwner=null;memoryPrincipal=null;memorySession="";memoryRevision++;memoryListeners.forEach(listener=>listener());}

export function usePulseAttempt(subject: {
    type: "company" | "contact";
    key: string;
} | null) {
    const api = useApiClient();
    const { orgId, session, activeMembership, supabase, subscribeScopeInvalidation } = useSession();
    useSyncExternalStore(subscribeMemory, memorySnapshot);
    const principal = session?.user.id;
    const sessionIdentity = session?.user.last_sign_in_at;
    const type = subject?.type, key = subject?.key;
    const controller = (() => {
        if (scopeRegistration && scopeRegistration !== subscribeScopeInvalidation) clearMemory();
        const owner = orgId && principal && activeMembership?.organizations?.status === "active" ? `${orgId}:${principal}:${sessionIdentity ?? ""}` : null;
        if (owner !== memoryOwner) {
            clearMemory();
            memoryOwner = owner;memoryPrincipal=principal??null;memorySession=sessionIdentity??"";
        }
        if (!owner || !type || !key)
            return null;
        let canonicalKey: string;
        try {
            canonicalKey = type === "company" ? canonicalDomain(key) : key.trim().toLowerCase();
        }
        catch {
            return null;
        }
        const context = `${type}:${canonicalKey}`;
        let c = attempts.get(context);
        if (!c) {
            c = new PulseAttemptController(api, orgId!, { type, key });
            attempts.set(context, c);
        }
        return c;
    })();
    useEffect(() => {
        if (scopeRegistration !== subscribeScopeInvalidation) {
            scopeWatch?.();
            scopeRegistration = subscribeScopeInvalidation;
            scopeWatch = subscribeScopeInvalidation(clearMemory);
        }
        if (watchClient !== supabase) {
            const generation = ++authGeneration;
            authWatch?.unsubscribe();
            watchClient = supabase;
            authWatch = supabase.auth.onAuthStateChange((_event, next) => {
                if (generation !== authGeneration) return;
                if (memoryOwner && (!next || next.user.id !== memoryPrincipal || (next.user.last_sign_in_at ?? "") !== memorySession)) clearMemory();
            }).data.subscription;
        }
        return () => { if (controller) {
        controller.suspend();
        if (!controller.snapshot().attempt) {
            for (const [key, c] of attempts)
                if (c === controller) {
                    attempts.delete(key);
                    controller.dispose();
                }
        }
    } if(attempts.size===0){scopeWatch?.();scopeWatch=null;scopeRegistration=null;++authGeneration;authWatch?.unsubscribe();authWatch=null;watchClient=null;} }; }, [controller,supabase,subscribeScopeInvalidation]);
    const state = useSyncExternalStore(controller?.subscribe ?? (() => () => { }), controller?.snapshot ?? (() => INITIAL));
    return { state, controller };
}
